import { FC, Fragment, useEffect, useState } from 'react';

import {
  Flex,
  Text,
  Input,
  Button,
  Center,
  Popover,
  Spinner,
  Divider,
  useToast,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverFooter,
  PopoverArrow,
  PopoverCloseButton,
  Textarea,
  Stack,
  Box,
} from '@chakra-ui/react';

import { selectors } from 'store/slices/washhub';

import { getPackageItems, markAsInvoiced, refreshPrice } from 'api/order';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { Order } from 'types/order';
import { Status } from 'types/items';
import { MdRefresh } from 'react-icons/md';
import { formatCurrency } from 'utils/formatNumbers';
import { sumFeeAdjustments, toFeeRows } from 'constants/feeAdjustments';
import FeeAdjustmentsEditor from 'components/primitives/FeeAdjustmentsEditor';
import FormikTextArea from 'components/primitives/FormikTextArea';
import { Link } from 'react-router-dom';
import { useSites } from 'hooks';
import { Form, Formik } from 'formik';

export const Invoicing: FC<{
  order: Order;
  isInvoiced?: boolean;
  onClose: () => void;
}> = ({ order, isInvoiced, onClose }) => {
  const { data: sites = [] } = useSites();
  const selectedSite = sites?.find((s) => s._id === order?.site?._id);
  const invoiceNotes = order?.site?.invoiceNotes || '-';
  const billingDetails = order?.site?.billingDetails || '-';

  const initialValues = {
    invoiceNotes: order?.site?.invoiceNotes || '',
    billingDetails: order?.site?.deliveryInstructions || '',
  };

  const toast = useToast();
  const queryClient = useQueryClient();

  const [values, setValues] = useState({
    coke: order.cokeAdjustment || 0,
    fees: toFeeRows(order.feeAdjustments, order.feeAdjustment),
    memo: order?.adjustmentMemo || '',
  });

  useEffect(() => {
    setValues({
      coke: order.cokeAdjustment || 0,
      fees: toFeeRows(order.feeAdjustments, order.feeAdjustment),
      memo: order?.adjustmentMemo || '',
    });
  }, [order]);

  const currentWashhub = selectors.useSelectedWashHub();

  const { data, isFetching } = useQuery(`fetch-sku-items-${order._id}`, () =>
    getPackageItems(order._id),
  );

  const [pricingDetails, setPricingDetails] = useState<
    Array<{
      itemSku: string;
      deliveryPrice: number;
      replacementPrice: number;
      effectiveDate: string;
    }>
  >(order?.pricingDetails || []);

  const { mutate, isLoading } = useMutation(markAsInvoiced, {
    onSuccess: () => {
      toast({
        title: 'Order is updated',
        status: 'success',
      });

      queryClient.invalidateQueries(`${currentWashhub?._id}-fetch-orders`);

      queryClient.invalidateQueries(
        `fetch-orders-status-count-${currentWashhub?._id}`,
      );

      queryClient.invalidateQueries(`fetch-orders`);

      onClose();
    },
  });

  const {
    data: pricingDetailsData,
  } = useQuery(
    `fetch-pricing-details-${order._id}`,
    () => refreshPrice(order._id),
    { enabled: !!data },
  );

  const {
    mutate: refreshPriceMutation,
    isLoading: refreshPriceLoading,
  } = useMutation(refreshPrice, {
    onSuccess: (data) => {
      setPricingDetails(data?.pricingDetails || []);
    },
  });

  useEffect(() => {
    if (pricingDetailsData) {
      setPricingDetails(pricingDetailsData?.pricingDetails || []);
    }
  }, [pricingDetailsData]);

  const calTotals = (data || []).reduce(
    (acc, item) => {
      const deliveryPrice = pricingDetails?.find(
        (p) => p?.itemSku === item?.details?.sku?.items?.sku?._id,
      )?.deliveryPrice;

      const totalQuantity = item.details?.totalItems ?? 0;

      const totalPrice = Number(deliveryPrice || '0') * totalQuantity;
      acc.subTotalPrice = acc.subTotalPrice + totalPrice;
      acc.totalItemsDelivered = acc.totalItemsDelivered + totalQuantity;

      return acc;
    },
    { subTotalPrice: 0, totalItemsDelivered: 0 },
  );

  const totalFees = sumFeeAdjustments(values.fees);
  const totalPrice = calTotals.subTotalPrice + totalFees - values.coke;

  const ValidStatus = [Status.CLOSE, Status.PENDING_INVOICE];
  return (
    <Fragment>
      <Flex justify="flex-end">
        <Stack direction="row" gap="4" align="center">
          {refreshPriceLoading ? (
            <Button
              isLoading={refreshPriceLoading}
              isDisabled={refreshPriceLoading}
              loadingText="Refreshing..."
              variant="ghost"
              textColor="messenger.500"
              size="sm"
              _hover={{
                bg: 'white',
                color: 'messenger.900',
              }}>
              Click me
            </Button>
          ) : (
            <Button
              isDisabled={refreshPriceLoading}
              onClick={() => {
                refreshPriceMutation(order._id);
              }}
              variant="ghost"
              textColor="messenger.500"
              size="sm"
              _hover={{
                bg: 'white',
                color: 'messenger.900',
              }}>
              <MdRefresh size="22px" /> Refresh Price
            </Button>
          )}
        </Stack>
      </Flex>
      <Flex
        w="100%"
        mt="20px"
        gap="20px"
        px="10px"
        pb="10px"
        direction="column"
        overflow="auto"
        fontSize="14px">
        <Flex direction="column" gap="20px">
          <Flex flex={1} w="100%" direction="column">
            {isFetching ? (
              <Center>
                <Spinner />
              </Center>
            ) : (
              <Flex
                w="100%"
                minW="350px"
                direction="column"
                borderRadius="6px"
                overflow="hidden"
                fontSize="14px"
                color="support.500">
                <Flex
                  bg="surface.400"
                  px="20px"
                  py="5px"
                  align="center"
                  gap="20px"
                  fontWeight="600">
                  <Text py="10px" flex={1.5}>
                    Item SKU
                  </Text>
                  <Text py="10px" textAlign="left" flex={1}>
                    Items Delivered
                  </Text>
                  <Text py="10px" textAlign="left" flex={1}>
                    Delivery price
                  </Text>
                  <Text py="10px" textAlign="left" flex={1}>
                    Total price
                  </Text>
                </Flex>
                <Flex direction="column">
                  {data?.map((item, index) => {
                    const deliveryPrice = pricingDetails?.find(
                      (p) => p?.itemSku === item?.details?.sku?.items?.sku?._id,
                    )?.deliveryPrice;

                    const totalQuantity = item.details?.totalItems ?? 0;

                    const totalPrice =
                      Number(deliveryPrice || '0') * totalQuantity;

                    return (
                      <Flex
                        px="15px"
                        align="center"
                        gap="20px"
                        key={item.details.id}
                        bg={index % 2 === 0 ? 'surface.200' : 'surface.100'}>
                        <Text py="10px" flex={1.5}>
                          {item.details.sku.items.sku.name || ''}
                        </Text>
                        <Text py="10px" textAlign="left" flex={1}>
                          {totalQuantity}
                        </Text>
                        <Text py="10px" textAlign="left" flex={1}>
                          {typeof deliveryPrice === 'number'
                            ? `${formatCurrency(deliveryPrice)}`
                            : '-'}
                        </Text>
                        <Text py="10px" textAlign="left" flex={1}>
                          {formatCurrency(totalPrice)}
                        </Text>
                      </Flex>
                    );
                  })}
                </Flex>
                <Flex
                  bg="surface.400"
                  px="20px"
                  py="5px"
                  align="center"
                  gap="20px"
                  fontWeight="600">
                  <Text py="10px" flex={1.5}>
                    Total
                  </Text>
                  <Text py="10px" textAlign="left" flex={1}>
                    {calTotals.totalItemsDelivered}
                  </Text>
                  <Text py="10px" textAlign="left" flex={1}>
                    -
                  </Text>
                  <Text py="10px" textAlign="left" flex={1}>
                    {formatCurrency(calTotals.subTotalPrice || '0')}
                  </Text>
                </Flex>
              </Flex>
            )}
          </Flex>
          <Flex direction="row" mt="20px" w="100%" gap="40px" fontSize="16px">
            <Flex direction="column" gap="20px" flex={1.5}>
              <Flex direction="column" gap="5px" height="75%">
                <Text>Invoice Adjustment Memo</Text>
                <Textarea
                  h="100px"
                  textAlign="left"
                  verticalAlign="top"
                  resize="none"
                  value={values?.memo}
                  defaultValue={order?.adjustmentMemo ?? ''}
                  onWheel={(e: any) => e.target?.blur?.()}
                  onChange={(e) => {
                    setValues((prev) => ({
                      ...prev,
                      memo: String(e.target.value),
                    }));
                  }}
                />
              </Flex>
            </Flex>

            <Flex maxW="260px" gap="10px" flex={1} direction="column">
              <Flex flex={1} gap="10px" align="center">
                <Text flex={1}>Subtotal</Text>
                <Text flex={0.5}>
                  {formatCurrency(calTotals.subTotalPrice || '0')}
                </Text>
              </Flex>
              {order?.site?.hasCokeAdjustment && (
                <Flex flex={1} gap="10px" align="center">
                  <Text flex={1}>Coke Adjustments</Text>
                  <Text fontWeight="bold">-</Text>
                  <Input
                    flex={0.5}
                    maxW="100px"
                    type="number"
                    defaultValue={order?.cokeAdjustment || 0}
                    onWheel={(e: any) => e.target?.blur?.()}
                    onChange={(e) => {
                      setValues((prev) => ({
                        ...prev,
                        coke: Number(e.target.value),
                      }));
                    }}
                  />
                </Flex>
              )}
              <FeeAdjustmentsEditor
                rows={values.fees}
                onChange={(fees) => setValues((prev) => ({ ...prev, fees }))}
              />
              <Divider />
              <Flex flex={1} gap="10px" align="center">
                <Text flex={1}>Total</Text>
                <Text flex={0.5}>{formatCurrency(totalPrice)}</Text>
              </Flex>
              <Flex gap={2} justifyContent="right">
                <Button
                  isLoading={isLoading}
                  onClick={() => {
                    mutate({
                      orderId: order._id,
                      cokeAdjustment: values.coke,
                      feeAdjustment: totalFees,
                      feeAdjustments: values.fees,
                      adjustmentMemo: values?.memo,
                    });
                  }}>
                  Save
                </Button>
                {ValidStatus.includes(order.status) &&
                  (isInvoiced ? (
                    <Popover>
                      {({ onClose }) => (
                        <>
                          <PopoverTrigger>
                            <Button isLoading={isLoading} variant="outline">
                              UnMark as Invoiced
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent>
                            <PopoverArrow />
                            <PopoverCloseButton />
                            <PopoverBody my="20px">
                              <Text textAlign="center">
                                Do you also want to move the order back to
                                Pending Invoice?
                              </Text>
                            </PopoverBody>
                            <PopoverFooter
                              display="flex"
                              justifyContent="center">
                              <Button
                                mr="10px"
                                onClick={() => {
                                  mutate({
                                    invoiced: false,
                                    orderId: order._id,
                                    status: Status.PENDING_INVOICE,
                                  });

                                  onClose();
                                }}>
                                Yes
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => {
                                  mutate({
                                    invoiced: false,
                                    orderId: order._id,
                                    status: Status.CLOSE,
                                  });

                                  onClose();
                                }}>
                                No
                              </Button>
                            </PopoverFooter>
                          </PopoverContent>
                        </>
                      )}
                    </Popover>
                  ) : (
                    <Button
                      isLoading={isLoading}
                      onClick={() => {
                        mutate({
                          invoiced: true,
                          orderId: order._id,
                          status: Status.CLOSE,
                          cokeAdjustment: values.coke,
                          feeAdjustment: totalFees,
                          feeAdjustments: values.fees,
                          adjustmentMemo: values?.memo,
                        });
                      }}>
                      Mark as Invoiced
                    </Button>
                  ))}
              </Flex>
            </Flex>
          </Flex>
          <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            onSubmit={() => {}}>
            <Form>
              <Flex justify="space-between" mt="20px" gap="20px">
                <Box flex={1} w="100%" minW={0}>
                  <FormikTextArea
                    name="invoiceNotes"
                    label="Invoice Notes"
                    description={
                      <>
                        <Link
                          to={`/customer-sites/${selectedSite?._id}`}
                          style={{ color: '#2FB64A', fontWeight: 600 }}>
                          Edit in customer details
                        </Link>
                      </>
                    }
                    value={invoiceNotes}
                    isReadOnly
                  />
                </Box>
                <Box flex={1} w="100%" minW={0}>
                  <FormikTextArea
                    name="billingDetails"
                    label="Billing Details"
                    description={
                      <>
                        <Link
                          to={`/customer-sites/${selectedSite?._id}`}
                          style={{ color: '#2FB64A', fontWeight: 600 }}>
                          Edit in customer details
                        </Link>
                      </>
                    }
                    value={billingDetails}
                    isReadOnly
                  />
                </Box>
              </Flex>
            </Form>
          </Formik>
        </Flex>
      </Flex>
    </Fragment>
  );
};
